import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, CircleHelp, Ellipsis, TriangleAlert, type LucideIcon } from "lucide-react";

import type { Factura, Movimiento } from "../api/tipos.ts";
import type { Decision } from "../ganchos/datos.ts";
import { fechaCorta, pesos, soloHora } from "../formato.ts";
import { ORIGEN } from "../vocabulario.ts";
import { atajoDe, cargaDeAtajo, rotuloDeAtajo, type Via } from "./atajo-de-pago.ts";
import { cargaDecision } from "./decision-modelo.ts";
import { ejesDe, type Cierre } from "./ejes.ts";
import { Encuentro } from "./Encuentro.tsx";
import { Boton, cx } from "./ui.tsx";

/**
 * Un pago de la bandeja, en una fila.
 *
 * La tarjeta completa medía unos 540 px: dos pagos por pantalla, ochenta
 * pantallas para los 168 de Lorus (30-sep-2026). La fila mide unos 50 y deja
 * la tarjeta para cuando hay que pensar: la flecha la abre ahí mismo.
 *
 * Cada dato en su columna y con su rótulo. Probado en maqueta: «JOHN CARMONA →
 * CAROLINA CARMONA · FVEL1915» seguido en una línea no se sabía dónde acababa
 * un nombre y empezaba el otro. Los colores son los de la tarjeta —azul lo que
 * dice el banco, verde lo que esperabas— para que abrirla no cambie el código.
 */
export function FilaPago({
  movimiento: m,
  cartera,
  decidiendo,
  puedeDecidir,
  alDecidir,
  pedido = false,
  activa = false,
  seleccionable = false,
  seleccionado = false,
  alSeleccionar,
  comando,
}: {
  movimiento: Movimiento;
  cartera: Factura[];
  decidiendo?: boolean;
  puedeDecidir: boolean;
  alDecidir: (d: Decision) => void;
  /** El pago que pidió el enlace del aviso: se abre y se trae a la vista. */
  pedido?: boolean;
  /** La fila donde está el teclado (SPEC-029 §14). */
  activa?: boolean;
  /** Puede confirmarse en lote: todo lo comprobable coincide. */
  seleccionable?: boolean;
  seleccionado?: boolean;
  alSeleccionar?: (marcado: boolean) => void;
  /** Una orden del teclado; `n` cambia en cada pulsación. */
  comando?: { accion: "abrir"; n: number };
}) {
  const [abierta, setAbierta] = useState(pedido);
  const fila = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (!pedido) return;
    setAbierta(true);
    fila.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [pedido]);
  useEffect(() => {
    if (activa) fila.current?.scrollIntoView({ block: "nearest" });
  }, [activa]);
  useEffect(() => {
    if (comando?.accion === "abrir") setAbierta((v) => !v);
  }, [comando]);
  const [inicio, setInicio] = useState<"nota" | "sin-factura" | undefined>();
  const atajo = atajoDe(m);
  const carga = cargaDeAtajo(m, atajo);
  const via = atajo.tipo === "evidencia" ? atajo.via : atajo;

  const abrir = (como?: "nota" | "sin-factura") => {
    setInicio(como);
    setAbierta(true);
  };
  const decide = (accion: "RECHAZAR" | "DESCARTAR") => alDecidir(cargaDecision({ clave: m.clave, accion }));

  // Lo que no es la acción recomendada va al menú ⋯, con su nombre entero. En
  // la fila «No» y «No es nuestro» parecían lo mismo y hacían cosas distintas
  // (SPEC-029 §4.4): ahora cada una dice qué pasa con el pago.
  const opciones: { texto: string; alPulsar: () => void }[] = [];
  if (via.tipo === "identificar" && !via.conflicto) {
    opciones.push({ texto: "No es un pago nuestro", alPulsar: () => decide("DESCARTAR") });
  } else if (via.tipo !== "revisar" && via.tipo !== "identificar") {
    opciones.push({ texto: "No es de este cliente", alPulsar: () => decide("RECHAZAR") });
  }
  if (via.tipo === "sugerida" || via.tipo === "varias") {
    opciones.push({ texto: "Confirmar sin factura…", alPulsar: () => abrir("sin-factura") });
  }
  if (via.tipo !== "revisar") opciones.push({ texto: "Añadir nota", alPulsar: () => abrir("nota") });
  const hora = soloHora(m.fecha);

  return (
    <li
      ref={fila}
      className={cx(
        "scroll-mt-4 border-t border-borde first:border-t-0",
        pedido && "bg-marca-suave/40",
        activa && "relative z-10 ring-2 ring-marca/50 ring-inset",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 @3xl:grid @3xl:grid-cols-[9.5rem_minmax(0,1fr)_8.5rem_4.5rem_auto]">
        <div className="flex min-w-24 items-start gap-2.5">
          {alSeleccionar && (
            <input
              type="checkbox"
              checked={seleccionado}
              disabled={!seleccionable}
              onChange={(e) => alSeleccionar(e.target.checked)}
              aria-label={`Incluir el pago de ${pesos(m.montoCentavos)} en el lote`}
              title={seleccionable ? "Incluir en el lote" : "Este hay que revisarlo: no entra en el lote"}
              className="mt-1 size-4 shrink-0 accent-[var(--color-marca)] disabled:opacity-25"
            />
          )}
          <div>
          <p className="cifra text-[15px] font-semibold text-tinta">{pesos(m.montoCentavos)}</p>
          <p className="text-[11px] whitespace-nowrap text-tenue-2">
            {fechaCorta(m.fecha)}
            {hora ? ` · ${hora}` : ""}
          </p>
          </div>
        </div>

        <div className="min-w-0 basis-full text-[13px] leading-snug @3xl:basis-auto">
          <p className="truncate">
            <span className="inline-block w-12 text-[11px] font-medium text-dato-tinta">Banco</span>
            {m.contraparte ? (
              <span className="text-tinta">{m.contraparte}</span>
            ) : (
              <span className="text-tenue-2">{m.sucursal ? `consignación en ${m.sucursal}` : "no dice quién"}</span>
            )}
          </p>
          <p className="truncate">
            <span className="inline-block w-12 text-[11px] font-medium text-exito-tinta">Cliente</span>
            <ClienteDe m={m} atajo={via} />
          </p>
        </div>

        <div className="min-w-0">
          <CeldaFactura m={m} atajo={via} />
        </div>

        <Coincidencias m={m} />

        <div className="ml-auto flex items-center gap-1.5">
          {puedeDecidir && (
            <>
              <Boton
                tamano="sm"
                // «Revisar» no confirma nada: se ve como lo que es, abrir.
                variante={carga ? "primario" : "suave"}
                icono={carga ? Check : undefined}
                cargando={decidiendo}
                onClick={() => (carga ? alDecidir(carga) : abrir())}
              >
                {rotuloDeAtajo(atajo)}
              </Boton>
              {opciones.length > 0 && <Menu opciones={opciones} />}
            </>
          )}
          <button
            type="button"
            onClick={() => (abierta ? setAbierta(false) : abrir())}
            aria-expanded={abierta}
            aria-label={abierta ? "Cerrar el detalle" : "Ver el detalle"}
            className="grid size-8 place-items-center rounded-lg text-tenue hover:bg-superficie-2 hover:text-tinta"
          >
            {abierta ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </button>
        </div>
      </div>

      {abierta && (
        <div className="border-t border-borde bg-superficie-2/50 p-3">
          <Encuentro
            key={inicio ?? "normal"}
            plana
            movimiento={m}
            cartera={cartera}
            decidiendo={decidiendo}
            alDecidir={alDecidir}
            inicio={inicio}
          />
        </div>
      )}
    </li>
  );
}

function ClienteDe({ m, atajo }: { m: Movimiento; atajo: Via }) {
  if (atajo.tipo === "identificar") {
    return (
      <span className={atajo.conflicto ? "text-aviso-tinta" : "text-tenue-2"}>
        {atajo.conflicto ? "varios candidatos igual de buenos" : "sin propuesta"}
      </span>
    );
  }
  return m.cliente ? <span className="text-tinta">{m.cliente}</span> : <span className="text-tenue-2">—</span>;
}

/** La factura en su columna: se ve de un vistazo cuál está resuelta y cuál no. */
function CeldaFactura({ m, atajo }: { m: Movimiento; atajo: Via }) {
  switch (atajo.tipo) {
    case "trae":
      return (
        <span className="cifra inline-block max-w-full truncate rounded-md border border-borde-fuerte bg-superficie-2 px-1.5 py-px text-xs text-tinta">
          {atajo.factura}
        </span>
      );
    case "sugerida":
      // Punteada: la propone la cartera, nadie la ha elegido todavía.
      return (
        <span
          title="Sugerida por tu cartera. Se liga al confirmar."
          className="cifra inline-block max-w-full truncate rounded-md border border-dashed border-marca/60 px-1.5 py-px text-xs text-marca-tinta"
        >
          ¿{atajo.factura.numero}?
        </span>
      );
    case "varias":
      return <span className="text-xs font-medium text-aviso-tinta">{atajo.candidatas.length} posibles</span>;
    case "ninguna":
      return <span className="text-xs text-tenue-2">sin factura</span>;
    case "revisar":
      return <span className="text-xs font-medium text-riesgo-tinta">{ORIGEN[m.origen].texto}</span>;
    default:
      return null;
  }
}

const ICONO: Record<Cierre, LucideIcon> = { cierra: Check, hueco: TriangleAlert, ausente: CircleHelp };
const COLOR: Record<Cierre, string> = { cierra: "text-marca", hueco: "text-aviso", ausente: "text-tenue-2" };
const LEIDO: Record<Cierre, string> = { cierra: "coincide", hueco: "no coincide del todo", ausente: "sin comprobar" };

/** Nombre, monto, fecha y factura, en ese orden. El título dice cada uno en palabras. */
function Coincidencias({ m }: { m: Movimiento }) {
  const ejes = ejesDe(m);
  if (!ejes.length) return <span className="hidden @3xl:block" />;
  return (
    <div
      className="flex gap-0.5"
      title={ejes.map((e) => `${e.etiqueta}: ${e.detalle}`).join("\n")}
    >
      {ejes.map((e) => {
        const Icono = ICONO[e.cierre];
        return (
          <span key={e.clave}>
            <Icono className={cx("size-3.5", COLOR[e.cierre])} aria-hidden />
            <span className="sr-only">
              {e.etiqueta}: {LEIDO[e.cierre]}, {e.detalle}.
            </span>
          </span>
        );
      })}
    </div>
  );
}

/** Las acciones que no son la recomendada. Fuera de la vista para que el sí sugerido sea el único a mano. */
function Menu({ opciones }: { opciones: { texto: string; alPulsar: () => void }[] }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setAbierto(false);
      }}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label="Más acciones"
        onClick={() => setAbierto((v) => !v)}
        className="grid size-8 place-items-center rounded-lg text-tenue hover:bg-superficie-2 hover:text-tinta"
      >
        <Ellipsis className="size-4" />
      </button>
      {abierto && (
        <div
          role="menu"
          className="absolute top-full right-0 z-20 mt-1 w-52 rounded-xl border border-borde bg-superficie p-1 shadow-tarjeta"
        >
          {opciones.map((o) => (
            <button
              key={o.texto}
              role="menuitem"
              type="button"
              onClick={() => {
                setAbierto(false);
                o.alPulsar();
              }}
              className="block w-full rounded-lg px-3 py-1.5 text-left text-sm text-tinta hover:bg-superficie-2"
            >
              {o.texto}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
